import { create } from 'zustand'
import { IUser, IUserProfile, AuthFetchStatus } from '../types'
import { pb } from '../pocketbase/Pocketbase'
import { fetchUserById, fetchUserProfile } from '../api/users'

interface IAuthState {
  user: IUser | null
  userProfile: IUserProfile | null
  isAdmin: boolean
  isInitializing: boolean
  setUser: (user: IUser) => void
  setUserProfile: (profile: IUserProfile | null) => void
  refetchUser: () => Promise<AuthFetchStatus>
  initializeAuth: () => Promise<void>
  reset: () => void
}

export const useAuthStore = create<IAuthState>((set, get) => ({
  user: null,
  userProfile: null,
  isAdmin: false,
  isInitializing: true,
  setUser: (user: IUser) => {
    set({ user, isAdmin: user.role === 'admin' })
  },
  setUserProfile: (profile: IUserProfile | null) => {
    set({ userProfile: profile })
  },
  refetchUser: async (): Promise<AuthFetchStatus> => {
    const userRecord = pb.authStore.record
    if (pb.authStore.isValid && userRecord != null) {
      try {
        const latestUser = await fetchUserById(userRecord.id)

        // Check if user has requested deletion
        if (latestUser.deleteRequestedAt) {
          console.warn('User account is scheduled for deletion')
          pb.authStore.clear()
          get().reset()
          return AuthFetchStatus.Deleted
        }

        const latestProfile = await fetchUserProfile(userRecord.id)
        set({ user: latestUser, userProfile: latestProfile, isAdmin: latestUser.role === 'admin' })
        return AuthFetchStatus.Success
      } catch (error: any) {
        // User was deleted or is no longer valid
        if (error?.status === 404 || error?.status === 401 || error?.status === 403) {
          console.warn('User no longer exists in database')
          pb.authStore.clear()
          get().reset()
          return AuthFetchStatus.Unauthorized
        }
        return AuthFetchStatus.Error
      }
    }
    return AuthFetchStatus.Unauthorized
  },
  initializeAuth: async () => {
    // Load from cookie on startup
    pb.authStore.loadFromCookie(document.cookie)

    // Save changes back to cookie
    pb.authStore.onChange(() => {
      document.cookie = pb.authStore.exportToCookie({ httpOnly: false })
    })

    await get().refetchUser()
    set({ isInitializing: false })
  },
  reset: () => {
    set({ user: null, userProfile: null, isAdmin: false, isInitializing: false })
  },
}))
